'use client';

/**
 * AppShell Provider
 * Provides centralized state for the entire layout system
 *
 * This provider:
 * - Receives initial values from server components
 * - Resolves current route from pathname
 * - Manages sidebar state
 * - Handles auth state synchronization
 */

import logger from '@/lib/logger';
import {
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  startTransition,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { AppShellContext } from './AppShellContext';
import { failedAuthContextStates } from './failClosedAuthState';
import {
  AuthRefreshGate,
  CONTEXT_FETCH_TIMEOUT_MS,
  readContextWithTimeout,
  shouldRevalidateOnReturn,
  withReadTimeout,
  type RefreshTicket,
} from './authRefreshGate';
import { HeaderActionsProvider } from './header/HeaderActionsContext';
import { NotificationInitializer } from '@/components/notifications';
import { MasqueradeBanner } from './MasqueradeBanner';
import { TierSimulatorBanner } from './TierSimulatorBanner';
import { RadixPointerEventsGuard } from './RadixPointerEventsGuard';
import { invalidateAllDedupedFetches } from '@/lib/utils/dedupedFetch';
import { routeRegistry } from '@/lib/routes/registry';
import { generateBreadcrumbs } from '@/lib/routes/breadcrumbs';
import {
  FEATURE_FLAGS_STORAGE_KEY,
  DEFAULT_FEATURE_FLAGS,
  type FeatureFlagState,
} from '@/lib/features';
import type {
  AppShellContextValueWithAuth,
  ResolvedRoute,
  LayoutVariant,
  RouteParams,
  AuthContext,
  AppUserRole,
  AdminStatus,
  AdminRole,
  PermissionsState,
  AccessControlState,
} from '@/lib/routes/types';
import type { UserSettingsContext, AgencyBranding, PlatformRole, BillingTier } from '@/lib/settings/types';
import type { UserContext, Role as AccessControlRole, UserAgencyMembership, UserDepartmentMembership } from '@/lib/access-control/types';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { useIdlePrefetch, getCriticalRoutes } from '@/hooks/useIdlePrefetch';
import {
  persistActiveOrgScopeCookies,
  resolveActiveOrgScope,
} from '@/lib/access-control/activeScope';

// ============================================================================
// Constants
// ============================================================================

const AUTH_DEBOUNCE_MS = 100; // Debounce auth state changes to prevent race conditions
const API_BASE = '/api/access-control';
const SIDEBAR_STATE_KEY = 'neutralaiz_sidebar_open';

// ============================================================================
// Props
// ============================================================================

interface AppShellProviderProps {
  children: ReactNode;

  /** Initial user from server-side auth check */
  initialUser: User | null;

  /** Initial user settings context from server */
  initialUserContext: UserSettingsContext | null;

  /** Initial agency branding from server */
  initialBranding: AgencyBranding | null;

  /** Initial feature flags */
  initialFeatureFlags?: Record<string, boolean>;
}

interface ApiRolePayload {
  id: string;
  name: string;
  slug?: string;
  display_name?: string;
  description?: string | null;
  privilege_level?: number;
  hierarchy_level?: number;
  is_system?: boolean;
  is_system_role?: boolean;
  is_assignable?: boolean;
  is_default?: boolean;
  color?: string | null;
  icon?: string | null;
  badge_url?: string | null;
  tenant_org_unit_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ApiAgencyPayload {
  agency_id: string;
  agency_name?: string;
  agency_slug?: string;
  agency?: UserAgencyMembership['agency'];
  roles?: ApiRolePayload[];
  permissions?: string[];
  billing_tier?: BillingTier;
}

interface ApiDepartmentPayload {
  department_id: string;
  department_name?: string;
  department_slug?: string;
  agency_id?: string;
  department?: UserDepartmentMembership['department'];
  roles?: ApiRolePayload[];
  permissions?: string[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Validate that a user object has the expected structure
 * Prevents "Cannot create property 'user' on string" errors when session data is corrupted
 */
function isValidUser(user: unknown): user is User {
  return (
    user !== null &&
    typeof user === 'object' &&
    !Array.isArray(user) &&
    typeof (user as Record<string, unknown>).id === 'string'
  );
}

/**
 * Convert Supabase User to AuthContext user type
 * Includes validation to prevent errors from corrupted session data
 */
function toAuthContextUser(supabaseUser: User | null): AuthContext['user'] {
  // Validate user object to prevent "Cannot create property 'user' on string" errors
  if (!isValidUser(supabaseUser)) {
    if (supabaseUser !== null) {
      logger.warn('AppShell', 'Invalid user object received', { data: typeof supabaseUser, supabaseUser });
    }
    return null;
  }

  const role = supabaseUser.app_metadata?.role as string | undefined;
  const platformRole = supabaseUser.app_metadata?.platform_role as string | undefined;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    role: (role as AppUserRole) || 'citizen',
    platformRole: platformRole as PlatformRole | undefined,
    user_metadata: supabaseUser.user_metadata,
    app_metadata: supabaseUser.app_metadata,
    aud: supabaseUser.aud,
    created_at: supabaseUser.created_at,
  };
}

// ============================================================================
// Provider Component
// ============================================================================

export function AppShellProvider({
  children,
  initialUser,
  initialUserContext,
  initialBranding,
  initialFeatureFlags = {},
}: AppShellProviderProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  // Route navigation changes the redirect target, not auth-subscription ownership.
  const pathnameRef = useRef(pathname);
  useEffect(() => { pathnameRef.current = pathname; }, [pathname]);

  // -------------------------------------------------------------------------
  // Auth State
  // -------------------------------------------------------------------------

  const [user, setUser] = useState<AuthContext['user']>(() => toAuthContextUser(initialUser));
  const [userContext, setUserContext] = useState<UserSettingsContext | null>(
    initialUserContext
  );
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  // -------------------------------------------------------------------------
  // Centralized Auth State (NEW - prevents multiple auth listeners)
  // -------------------------------------------------------------------------

  // Auth version counter - increments on every auth change to trigger dependent hooks
  const [authVersion, setAuthVersion] = useState(0);

  // Timestamp of last auth refresh (for coordination with other components)
  const [lastAuthRefresh, setLastAuthRefresh] = useState(Date.now());

  // Debounce timer ref for auth state changes
  const authHandlerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Guard to prevent processing duplicate SIGNED_OUT events (prevents infinite loops)
  const hasProcessedSignOut = useRef(false);

  // Track last auth event type to suppress redundant TOKEN_REFRESHED *and*
  // SIGNED_IN updates. Supabase auth-js fires SIGNED_IN on every tab
  // visibility recovery (not just on real login) — without seeding from
  // `initialUser`, the very first focus regain after page load slips past
  // the suppression guard, calls refreshCentralizedAuth, flips
  // access-control state to loading, and unmounts any PermissionGuard-
  // wrapped iframe (template editor, etc.) which then reloads from scratch.
  const lastAuthEventRef = useRef<string | null>(initialUser ? 'SIGNED_IN' : null);
  const lastAuthUserIdRef = useRef<string | null>(initialUser?.id ?? null);

  // One gate for every grant read (#75, Q-846 v4): sign-out / a user switch retires reads in flight, a same-user
  // refresh is not run twice, a different user's refresh is never dropped.
  const refreshGateRef = useRef<AuthRefreshGate>(new AuthRefreshGate());
  // The listener's user lookup/recovery starts before a context ticket exists.
  // Every newer event and listener cleanup retires that asynchronous work too.
  const authEventGenerationRef = useRef(0);
  // When the grants were last read (any outcome): paces the return-to-tab revalidation.
  const lastContextReadAtRef = useRef(0);

  // Centralized admin status
  const [adminStatus, setAdminStatus] = useState<AdminStatus>({
    isAdmin: false,
    isPlatformAdmin: false,
    isTenantAdmin: false,
    isLegacyAdmin: false,
    role: null,
    loading: true,
  });

  // Centralized permissions state
  const [permissionsState, setPermissionsState] = useState<PermissionsState>({
    permissions: [],
    roles: [],
    tier: 'trial' as BillingTier,
    loading: true,
    error: null,
  });

  // Centralized access control state
  const [accessControlState, setAccessControlState] = useState<AccessControlState>({
    userContext: null,
    loading: true,
    error: null,
    activeAgencyId: null,
    activeDepartmentId: null,
    isMasquerading: false,
    masqueradeRoleIds: [],
    masqueradeRoleNames: [],
  });

  // -------------------------------------------------------------------------
  // Layout State
  // -------------------------------------------------------------------------

  // Initialize with server-safe defaults to avoid hydration mismatch (#418).
  // Browser-specific values (localStorage, matchMedia) are loaded in useEffect.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [layoutOverride, setLayoutOverride] = useState<LayoutVariant | null>(null);

  // Hydrate sidebar state from localStorage/matchMedia after mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STATE_KEY);
      if (stored !== null) {
        setSidebarOpen(JSON.parse(stored));
        return;
      }
    } catch {}
    setSidebarOpen(window.matchMedia('(min-width: 1280px)').matches);
  }, []);

  // -------------------------------------------------------------------------
  // Branding & Features
  // -------------------------------------------------------------------------

  const [agencyBranding] = useState<AgencyBranding | null>(initialBranding);

  // Feature flags - server-safe default, hydrated from localStorage after mount
  const [featureFlags, setFeatureFlagsState] = useState<Record<string, FeatureFlagState | boolean>>(
    () => ({ ...DEFAULT_FEATURE_FLAGS, ...initialFeatureFlags })
  );

  // Hydrate feature flags from localStorage after mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFeatureFlagsState(prev => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      logger.warn('AppShellProvider', 'Failed to load feature flags from localStorage', { error });
    }
  }, []);

  // -------------------------------------------------------------------------
  // Idle Prefetch - Pre-warm pages during browser idle time (DEV ONLY)
  // -------------------------------------------------------------------------

  // Only enable in dev mode - production builds are pre-compiled at build time
  const isDev = process.env.NODE_ENV === 'development';
  useIdlePrefetch({
    routes: getCriticalRoutes(),
    enabled: isDev && !!user && typeof window !== 'undefined',
    delay: 3000,      // Wait 3s after page load before starting
    staggerDelay: 500, // 500ms between each route prefetch
  });

  // -------------------------------------------------------------------------
  // Route Resolution
  // -------------------------------------------------------------------------

  // Initialize route synchronously to prevent layout flash
  // This is critical for pages with layout: 'public' that should not show a header
  const [currentRoute, setCurrentRoute] = useState<ResolvedRoute | null>(() => {
    const config = routeRegistry.getByPath(pathname);
    if (!config) return null;

    const params = routeRegistry.extractParams(pathname, config.path);
    const searchParamsObj = Object.fromEntries(searchParams.entries());

    // For sync resolution, use static title or placeholder
    // Dynamic titles will be resolved in useEffect
    const resolvedTitle = typeof config.title === 'function' ? 'Page' : config.title;

    return {
      ...config,
      params,
      searchParams: searchParamsObj,
      fullPath: pathname,
      breadcrumbs: [], // Breadcrumbs resolved async below
      resolvedTitle,
    };
  });

  // Update route when pathname changes
  // First update synchronously with layout info to prevent flash, then async for breadcrumbs/title
  useEffect(() => {
    const config = routeRegistry.getByPath(pathname);

    if (!config) {
      setCurrentRoute(null);
      return;
    }

    // Extract params from path
    const params = routeRegistry.extractParams(pathname, config.path);
    const searchParamsObj = Object.fromEntries(searchParams.entries());

    // SYNC UPDATE: Set route immediately with layout info to prevent header flash
    // This ensures the correct layout is used before any async operations complete
    const syncRoute: ResolvedRoute = {
      ...config,
      params,
      searchParams: searchParamsObj,
      fullPath: pathname,
      breadcrumbs: [], // Will be populated by async update
      resolvedTitle: typeof config.title === 'function' ? 'Page' : config.title,
    };
    setCurrentRoute(syncRoute);

    // ASYNC UPDATE: Resolve breadcrumbs and dynamic title
    // Capture config in closure to avoid TypeScript narrowing issues
    const routeConfig = config;
    async function resolveAsyncData() {
      const routeParams: RouteParams = {
        params,
        searchParams: searchParamsObj,
      };

      // Generate breadcrumbs
      const breadcrumbs = await generateBreadcrumbs(pathname, routeParams);

      // Resolve dynamic title if needed
      let resolvedTitle: string | undefined;
      if (typeof routeConfig.title === 'function') {
        try {
          resolvedTitle = await routeConfig.title(routeParams);
        } catch (error) {
          logger.error('AppShellProvider', 'Failed to resolve route title', { error });
          resolvedTitle = 'Page';
        }
      } else {
        resolvedTitle = routeConfig.title;
      }

      // Update with async data
      const resolved: ResolvedRoute = {
        ...routeConfig,
        params,
        searchParams: searchParamsObj,
        fullPath: pathname,
        breadcrumbs,
        resolvedTitle,
      };
      setCurrentRoute(resolved);
    }

    resolveAsyncData();
  }, [pathname, searchParams]);

  // -------------------------------------------------------------------------
  // Centralized Auth Functions
  // (Defined before useEffect hooks that use them)
  // -------------------------------------------------------------------------

  /**
   * Compute admin status from user metadata
   * Called when auth state changes
   */
  const computeAdminStatus = useCallback((currentUser: AuthContext['user']) => {
    if (!currentUser) {
      setAdminStatus({
        isAdmin: false,
        isPlatformAdmin: false,
        isTenantAdmin: false,
        isLegacyAdmin: false,
        role: null,
        loading: false,
      });
      return;
    }

    const platformRole = currentUser.platformRole || currentUser.app_metadata?.platform_role;
    const legacyRole = currentUser.role;

    const isPlatformAdmin = platformRole === 'admin-platform';
    const isTenantAdmin = platformRole === 'admin-tenant';
    const isLegacyAdmin = legacyRole === 'admin';
    const isAdmin = isPlatformAdmin || isTenantAdmin || isLegacyAdmin;

    let role: AdminRole = null;
    if (isPlatformAdmin) role = 'admin-platform';
    else if (isTenantAdmin) role = 'admin-tenant';
    else if (isLegacyAdmin) role = 'legacy-admin';

    setAdminStatus({
      isAdmin,
      isPlatformAdmin,
      isTenantAdmin,
      isLegacyAdmin,
      role,
      loading: false,
    });

    logger.debug('AppShell', 'Admin status computed', { isAdmin, role });
  }, []);

  /**
   * Fetch unified auth context from API — single fetch populates both
   * permissionsState and accessControlState from one /api/access-control/context call.
   */
  const readUnifiedContext = useCallback(async (ticket: RefreshTicket, silent: boolean) => {
    // Q-846 v4: every write below first checks the ticket. A read retired by sign-out, a user switch or a newer
    // read must never write its (old user's) grants back.
    if (!silent) {
      setPermissionsState((prev) => ({ ...prev, loading: true, error: null }));
      setAccessControlState((prev) => ({ ...prev, loading: true, error: null }));
    }

    // ONE 15 s budget for the whole read (Q-846 v4): the user check AND the context read, so neither can hang
    // with the previous grants still in place.
    const startedAt = Date.now();
    try {
      const supabase = createClient();
      const { data: { user: currentUser }, error: authError } = await withReadTimeout(
        supabase.auth.getUser(),
        CONTEXT_FETCH_TIMEOUT_MS,
        'Auth user read',
      );

      if (authError) {
        throw new Error(`Authentication error: ${authError.message}`);
      }

      if (!currentUser) {
        if (!ticket.isCurrent()) return;
        setPermissionsState({
          permissions: [],
          roles: [],
          tier: 'trial' as BillingTier,
          loading: false,
          error: null,
        });
        setAccessControlState({
          userContext: null,
          loading: false,
          error: null,
          activeAgencyId: null,
          activeDepartmentId: null,
          isMasquerading: false,
          masqueradeRoleIds: [],
          masqueradeRoleNames: [],
        });
        computeAdminStatus(null); // no user, no admin grant either
        return;
      }

      // ONE fetch to /api/access-control/context
      // Bounded (Q-846 v4): headers AND body under one timer, so a read that never finishes fails closed instead of
      // keeping the previous grants.
      const { response, data: body } = await readContextWithTimeout(
        `${API_BASE}/context`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
        Math.max(1, CONTEXT_FETCH_TIMEOUT_MS - (Date.now() - startedAt)),
      );

      if (!response.ok) {
        if (response.status === 401) throw new Error('Not authenticated');
        if (response.status === 403) throw new Error('Access denied');
        throw new Error(`Failed to fetch auth context: ${response.statusText}`);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the API payload is mapped field by field below
      const data = body as any;

      // Map API roles to full role format
      const mappedRoles = ((data.platform_roles || []) as ApiRolePayload[]).map((apiRole) => ({
        id: apiRole.id,
        tenant_org_unit_id: apiRole.tenant_org_unit_id ?? null,
        name: apiRole.display_name || apiRole.name,
        slug: apiRole.slug || apiRole.name.toLowerCase().replace(/\s+/g, '-'),
        description: apiRole.description ?? null,
        privilege_level: apiRole.privilege_level ?? apiRole.hierarchy_level ?? 0,
        is_system_role: apiRole.is_system_role ?? apiRole.is_system ?? false,
        is_assignable: apiRole.is_assignable ?? true,
        is_default: apiRole.is_default ?? false,
        color: apiRole.color ?? null,
        icon: apiRole.icon ?? null,
        badge_url: apiRole.badge_url ?? null,
        created_at: apiRole.created_at || '',
        updated_at: apiRole.updated_at || '',
      }));

      // Populate permissionsState from the same payload
      if (!ticket.isCurrent()) return;
      setPermissionsState({
        permissions: data.platform_permissions || [],
        roles: mappedRoles,
        tier: (data.effective_tier || 'trial') as BillingTier,
        loading: false,
        error: null,
      });

      // Map agencies to full UserAgencyMembership type
      const mappedAgencies: UserAgencyMembership[] = ((data.agencies || []) as ApiAgencyPayload[]).map((a) => ({
        agency_id: a.agency_id,
        agency_name: a.agency_name || 'Unknown',
        agency_slug: a.agency_slug || 'unknown',
        agency: a.agency || { id: a.agency_id, name: a.agency_name || 'Unknown', slug: a.agency_slug || 'unknown', status: 'active', billing_tier: a.billing_tier || 'trial', created_at: '', updated_at: '' },
        roles: (a.roles || []).map((r) => ({
          id: r.id,
          tenant_org_unit_id: r.tenant_org_unit_id ?? null,
          name: r.name,
          slug: r.slug || r.name?.toLowerCase().replace(/\s+/g, '-') || '',
          description: r.description ?? null,
          privilege_level: r.privilege_level ?? 0,
          is_system_role: r.is_system_role ?? false,
          is_assignable: r.is_assignable ?? true,
          is_default: r.is_default ?? false,
          color: r.color ?? null,
          icon: r.icon ?? null,
          badge_url: r.badge_url ?? null,
          created_at: r.created_at || '',
          updated_at: r.updated_at || '',
        })),
        permissions: a.permissions || [],
        billing_tier: (a.billing_tier || 'trial') as BillingTier,
      }));

      // Map departments to full UserDepartmentMembership type
      const mappedDepartments: UserDepartmentMembership[] = ((data.departments || []) as ApiDepartmentPayload[]).map((d) => ({
        department_id: d.department_id,
        department_name: d.department_name || 'Unknown',
        department_slug: d.department_slug || 'unknown',
        agency_id: d.agency_id || '',
        department: d.department || { id: d.department_id, agency_id: d.agency_id || '', name: d.department_name || 'Unknown', slug: d.department_slug || 'unknown', status: 'active', created_at: '', updated_at: '' },
        roles: (d.roles || []).map((r) => ({
          id: r.id,
          tenant_org_unit_id: r.tenant_org_unit_id ?? null,
          name: r.name,
          slug: r.slug || r.name?.toLowerCase().replace(/\s+/g, '-') || '',
          description: r.description ?? null,
          privilege_level: r.privilege_level ?? 0,
          is_system_role: r.is_system_role ?? false,
          is_assignable: r.is_assignable ?? true,
          is_default: r.is_default ?? false,
          color: r.color ?? null,
          icon: r.icon ?? null,
          badge_url: r.badge_url ?? null,
          created_at: r.created_at || '',
          updated_at: r.updated_at || '',
        })),
        permissions: d.permissions || [],
      }));

      // Build proper UserContext
      const userContextData: UserContext = {
        user_id: data.user_id,
        user_email: data.user_email,
        platform_roles: mappedRoles,
        platform_permissions: data.platform_permissions || [],
        agencies: mappedAgencies,
        departments: mappedDepartments,
        active_agency_id: data.active_agency_id || undefined,
        active_department_id: data.active_department_id || undefined,
        is_platform_admin: data.is_platform_admin || false,
        is_tenant_admin: data.is_tenant_admin || false,
        effective_tier: (data.effective_tier || 'trial') as BillingTier,
      };

      // Populate accessControlState from the same payload
      if (!ticket.isCurrent()) return;
      setAccessControlState((prev) => {
        const nextScope = resolveActiveOrgScope(userContextData, {
          activeAgencyId: prev.activeAgencyId,
          activeDepartmentId: prev.activeDepartmentId,
        });
        const scopedUserContext: UserContext = {
          ...userContextData,
          active_agency_id: nextScope.activeAgencyId || undefined,
          active_department_id: nextScope.activeDepartmentId || undefined,
        };

        return {
          userContext: scopedUserContext,
          loading: false,
          error: null,
          activeAgencyId: nextScope.activeAgencyId,
          activeDepartmentId: nextScope.activeDepartmentId,
          isMasquerading: data.is_masquerading || false,
          masqueradeRoleIds: data.masquerade_role_ids || [],
          masqueradeRoleNames: data.masquerade_role_names || [],
        };
      });

      // Update admin status from DB-backed data (not JWT guesses)
      const dbIsPlatformAdmin = data.is_platform_admin || false;
      const dbIsTenantAdmin = data.is_tenant_admin || false;
      if (!ticket.isCurrent()) return;
      setAdminStatus({
        isAdmin: dbIsPlatformAdmin || dbIsTenantAdmin || (data.platform_roles || []).some(
          (r: ApiRolePayload) => r.slug === 'admin' || r.name === 'admin'
        ),
        isPlatformAdmin: dbIsPlatformAdmin,
        isTenantAdmin: dbIsTenantAdmin,
        isLegacyAdmin: false, // No longer relevant when DB-backed
        role: dbIsPlatformAdmin ? 'admin-platform' :
              dbIsTenantAdmin ? 'admin-tenant' : null,
        loading: false,
      });

      logger.debug('AppShell', 'Unified auth context fetched', {
        userId: data.user_id,
        isPlatformAdmin: data.is_platform_admin,
        permissionCount: data.platform_permissions?.length || 0,
        roleCount: mappedRoles.length,
      });
    } catch (err) {
      logger.error('AppShell', 'Error fetching unified auth context', { error: err });
      // A retired read's failure must not clobber the state its successor (or sign-out) wrote.
      if (!ticket.isCurrent()) return;
      const error = err instanceof Error ? err : new Error('Failed to fetch auth context');
      // Fail closed (Q-846): never keep the previous grants beside an error; guards would keep granting stale roles.
      setAccessControlState((current) => failedAuthContextStates(error, current).accessControlState);
      const failed = failedAuthContextStates(error, { activeAgencyId: null, activeDepartmentId: null });
      setPermissionsState(failed.permissionsState);
      setAdminStatus(failed.adminStatus);
    } finally {
      lastContextReadAtRef.current = Date.now();
    }
  }, [computeAdminStatus]);

  /** Public refetch (refetchPermissions/refetchAccessControl): a read bound to the current session. */
  const fetchUnifiedContext = useCallback(
    () => readUnifiedContext(refreshGateRef.current.beginRead(), false),
    [readUnifiedContext],
  );

  /**
   * Refresh all centralized auth state
   * Called when authVersion changes
   */
  const refreshCentralizedAuth = useCallback(async (currentUser: AuthContext['user']) => {
    // The same user's refresh never runs twice; sign-out or a DIFFERENT user retires the running one and proceeds
    // (#75, Q-846 v4: a new user's refresh used to be dropped, leaving the previous user's grants in place).
    const ticket = refreshGateRef.current.start(currentUser?.id ?? null);
    if (!ticket) return;

    try {
      logger.debug('AppShell', 'Refreshing centralized auth state');

      // Fast initial admin status from JWT metadata (overwritten by DB data in fetchUnifiedContext)
      computeAdminStatus(currentUser);

      // Single unified fetch populates permissions + access control + admin status
      if (currentUser) {
        await readUnifiedContext(ticket, false);
      } else if (ticket.isCurrent()) {
        // Clear states for logged out user
        setPermissionsState({
          permissions: [],
          roles: [],
          tier: 'trial' as BillingTier,
          loading: false,
          error: null,
        });
        setAccessControlState({
          userContext: null,
          loading: false,
          error: null,
          activeAgencyId: null,
          activeDepartmentId: null,
          isMasquerading: false,
          masqueradeRoleIds: [],
          masqueradeRoleNames: [],
        });
      }

      // Only apply results if no sign-out invalidated us while we were in-flight
      if (ticket.isCurrent()) {
        // Update last refresh timestamp
        setLastAuthRefresh(Date.now());
      } else {
        logger.debug('AppShell', 'Discarding stale auth refresh (generation changed)');
      }
    } finally {
      refreshGateRef.current.finish(ticket);
    }
  }, [computeAdminStatus, readUnifiedContext]);

  /**
   * Synchronously clear all centralized auth state and invalidate in-flight refreshes.
   * Used on sign-out to guarantee immediate state cleanup (#75).
   */
  const clearCentralizedAuthState = useCallback(() => {
    // Retire every grant read in flight: none may write its results after this (#75, Q-846 v4)
    refreshGateRef.current.invalidate();

    // Synchronously clear all auth state
    computeAdminStatus(null);
    setPermissionsState({
      permissions: [],
      roles: [],
      tier: 'trial' as BillingTier,
      loading: false,
      error: null,
    });
    setAccessControlState({
      userContext: null,
      loading: false,
      error: null,
      activeAgencyId: null,
      activeDepartmentId: null,
      isMasquerading: false,
      masqueradeRoleIds: [],
      masqueradeRoleNames: [],
    });

    // Wipe in-memory dedup cache here too — covers the session-expiry path
    // (SIGNED_OUT event) which doesn't go through performLogout/clearAuthLocalStorage.
    // Without this, a returning user on a shared device could briefly see the previous
    // user's cached masquerade roles / tier-simulator state.
    invalidateAllDedupedFetches();

    logger.debug('AppShell', 'Cleared centralized auth state (sign-out)');
  }, [computeAdminStatus]);

  // -------------------------------------------------------------------------
  // Auth State Change Listener (DEBOUNCED to prevent race conditions)
  // -------------------------------------------------------------------------

  useEffect(() => {
    const supabase = createClient();

    const failCurrentAuthEvent = (error: Error) => {
      refreshGateRef.current.invalidate();
      lastAuthEventRef.current = 'SIGNED_OUT';
      lastAuthUserIdRef.current = null;
      setUser(null);
      setUserContext(null);
      setAuthVersion((v) => v + 1);
      // A failed authority lookup clears grants but retains the selected scope.
      setAccessControlState((current) => failedAuthContextStates(error, current).accessControlState);
      const failed = failedAuthContextStates(error, { activeAgencyId: null, activeDepartmentId: null });
      setPermissionsState(failed.permissionsState);
      setAdminStatus(failed.adminStatus);
      invalidateAllDedupedFetches();
    };

    // Debounced handler to prevent multiple rapid auth events from causing race conditions
    const handleAuthChange = (event: AuthChangeEvent, session: Session | null) => {
      // auth-js emits INITIAL_SESSION as a subscription snapshot after SDK
      // initialization/locking. Server initial state and the mount getUser/context
      // read already own bootstrap here. Unhandled snapshots must not cancel
      // the authoritative transition lookup that is currently in progress.
      if (event !== 'SIGNED_OUT' && event !== 'SIGNED_IN' && event !== 'TOKEN_REFRESHED') return;
      // Retire work immediately on receipt, before debounce or the first await.
      const eventGeneration = ++authEventGenerationRef.current;
      const isCurrentEvent = () => authEventGenerationRef.current === eventGeneration;
      // Clear any pending handler to debounce
      if (authHandlerTimeoutRef.current) {
        clearTimeout(authHandlerTimeoutRef.current);
      }

      // Debounce by AUTH_DEBOUNCE_MS
      authHandlerTimeoutRef.current = setTimeout(async () => {
        if (!isCurrentEvent()) return;
        const hasSession = !!session;
        logger.debug('AppShell', 'Auth state change (debounced)', { event, hasSession });

        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !hasSession)) {
          // Guard: Process SIGNED_OUT only once to prevent infinite loops
          if (hasProcessedSignOut.current) {
            logger.debug('AppShell', 'Duplicate SIGNED_OUT ignored');
            return;
          }

          // Before treating as sign-out, attempt a manual session refresh.
          // Backgrounded tabs throttle timers, causing auto-refresh to fail.
          // A manual refresh on tab return usually succeeds.
          if (event === 'TOKEN_REFRESHED' && !hasSession && initialUser) {
            try {
              logger.debug('AppShell', 'Token refresh returned no session — attempting manual refresh');
              const { data: refreshData } = await withReadTimeout(
                supabase.auth.refreshSession(), CONTEXT_FETCH_TIMEOUT_MS, 'Auth session recovery',
              );
              if (!isCurrentEvent()) return;
              if (refreshData?.session) {
                logger.info('AppShell', 'Manual session refresh succeeded — session restored');
                hasProcessedSignOut.current = false;
                return; // Session recovered, no sign-out needed
              }
            } catch (refreshErr) {
              if (!isCurrentEvent()) return;
              logger.warn('AppShell', 'Manual session refresh also failed', { error: refreshErr });
            }
          }

          hasProcessedSignOut.current = true;
          lastAuthEventRef.current = 'SIGNED_OUT';
          lastAuthUserIdRef.current = null;

          // Session expired or user signed out
          const newUser = null;

          // Use startTransition to batch these state updates
          startTransition(() => {
            setUser(newUser);
            setUserContext(null);
            setAuthVersion((v) => v + 1);
          });

          // Clear centralized auth state synchronously — no async fetch (#75)
          clearCentralizedAuthState();

          // Only redirect to login if user was previously authenticated
          // and not on a public page (citizen pages, login, etc.)
          const publicPaths = ['/login', '/auth', '/citizen', '/demo', '/calls/', '/incident-report'];
          const redirectPathname = pathnameRef.current;
          const isPublicPath = publicPaths.some(p => redirectPathname.startsWith(p));

          if (initialUser && !isPublicPath) {
            logger.warn('AppShell', 'Session expired, redirecting to login');
            const returnUrl = encodeURIComponent(redirectPathname);
            router.push(`/login?returnTo=${returnUrl}&reason=session_expired`);
          }
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Avoid reading fields directly from `session` object. New Supabase auth-js
          // can throw if legacy session properties are touched.
          let userResult;
          try {
            userResult = await withReadTimeout(
              supabase.auth.getUser(), CONTEXT_FETCH_TIMEOUT_MS, 'Auth event user read',
            );
          } catch (err) {
            if (!isCurrentEvent()) return;
            failCurrentAuthEvent(err instanceof Error ? err : new Error('Auth event user read failed'));
            return;
          }
          if (!isCurrentEvent()) return;
          const { data: { user: currentUser }, error: userError } = userResult;
          hasProcessedSignOut.current = false;

          if (userError) {
            logger.warn('AppShell', 'Auth change user refresh failed', { event, error: userError.message });
            failCurrentAuthEvent(new Error(`Auth event user read failed: ${userError.message}`));
            return;
          }

          if (currentUser && isValidUser(currentUser)) {
            const newUserId = currentUser.id;

            // Suppress redundant SIGNED_IN/TOKEN_REFRESHED events that don't
            // change the user. Supabase fires SIGNED_IN on every tab focus
            // regain via its visibilitychange listener; without this guard,
            // refreshCentralizedAuth runs on every focus, flipping
            // access-control state to loading and remounting any
            // PermissionGuard-wrapped iframe.
            const isSameUser =
              lastAuthEventRef.current !== 'SIGNED_OUT' &&
              lastAuthUserIdRef.current === newUserId;
            if (isSameUser && (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN')) {
              logger.debug('AppShell', `Suppressing redundant ${event} (same user)`);
              lastAuthEventRef.current = event;
              return;
            }

            // A DIFFERENT user (no SIGNED_OUT in between, by SIGNED_IN or TOKEN_REFRESHED): drop the previous user's
            // grants now, before the new read, so nothing renders with them meanwhile (Q-846 v4).
            const switchedUser = lastAuthUserIdRef.current !== null && lastAuthUserIdRef.current !== newUserId;
            if (switchedUser) {
              logger.debug('AppShell', 'User switched without sign-out; clearing previous grants');
              clearCentralizedAuthState();
            }

            lastAuthEventRef.current = event;
            lastAuthUserIdRef.current = newUserId;

            const newUser = toAuthContextUser(currentUser);

            // Use startTransition to batch these state updates and prevent
            // synchronous cascading re-renders that exhaust browser resources
            startTransition(() => {
              setUser(newUser);
              // Only increment auth version on SIGNED_IN (not every TOKEN_REFRESHED)
              if (event === 'SIGNED_IN') {
                setAuthVersion((v) => v + 1);
              }
            });

            // Refresh centralized auth state on SIGNED_IN, and on ANY event that switched the user (a TOKEN_REFRESHED
            // for a new user would otherwise leave them with the cleared, empty grants).
            if (event === 'SIGNED_IN' || switchedUser) {
              await refreshCentralizedAuth(newUser);
            }
          } else {
            // Authoritative no-user results for either event are signed out.
            // Refresh yielded no user, treat as signed-out session state.
            hasProcessedSignOut.current = true;
            lastAuthEventRef.current = 'SIGNED_OUT';
            lastAuthUserIdRef.current = null;
            startTransition(() => {
              setUser(null);
              setUserContext(null);
              setAuthVersion((v) => v + 1);
            });
            clearCentralizedAuthState();
          }
        }
      }, AUTH_DEBOUNCE_MS);
    };

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthChange);

    return () => {
      authEventGenerationRef.current += 1;
      // Cleanup debounce timer on unmount
      if (authHandlerTimeoutRef.current) {
        clearTimeout(authHandlerTimeoutRef.current);
      }
      subscription.unsubscribe();
    };
  }, [initialUser, router, refreshCentralizedAuth, clearCentralizedAuthState]);

  // Returning to the page re-reads the grants (Q-846 v4): a bfcache restore always, a tab becoming visible once
  // the last read is stale. Silent (no loading flip), so PermissionGuard-wrapped iframes are not remounted; a
  // failed read still fails closed.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const revalidate = (trigger: Parameters<typeof shouldRevalidateOnReturn>[0]) => {
      if (!user) return;
      if (!shouldRevalidateOnReturn(trigger, lastContextReadAtRef.current, Date.now())) return;
      void readUnifiedContext(refreshGateRef.current.beginRead(), true);
    };
    const onPageShow = (event: PageTransitionEvent) => revalidate({ kind: 'pageshow', persisted: event.persisted });
    const onVisibility = () => revalidate({ kind: 'visibilitychange', visible: document.visibilityState === 'visible' });
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, readUnifiedContext]);

  // -------------------------------------------------------------------------
  // Initial Centralized Auth Fetch
  // -------------------------------------------------------------------------

  // Fetch centralized auth state on mount if user exists
  useEffect(() => {
    if (user) {
      refreshCentralizedAuth(user);
    } else {
      // Mark as not loading if no user
      setAdminStatus((prev) => ({ ...prev, loading: false }));
      setPermissionsState((prev) => ({ ...prev, loading: false }));
      setAccessControlState((prev) => ({ ...prev, loading: false }));
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev: boolean) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const refreshAuth = useCallback(async () => {
    setIsAuthLoading(true);
    try {
      const response = await fetch('/api/auth/session');
      if (response.ok) {
        const data = await response.json();
        setUser(toAuthContextUser(data.user));
        setUserContext(data.context);
      }
    } catch (error) {
      logger.error('AppShellProvider', 'Failed to refresh auth', { error });
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  const setActiveAgency = useCallback((agencyId: string) => {
    setAccessControlState((prev) => {
      if (!prev.userContext) return prev;

      const isValidAgency =
        prev.userContext.agencies.some((agency) => agency.agency_id === agencyId) ||
        prev.userContext.departments.some((department) => department.agency_id === agencyId);

      if (!isValidAgency) {
        return prev;
      }

      return {
        ...prev,
        userContext: {
          ...prev.userContext,
          active_agency_id: agencyId,
          active_department_id: undefined,
        },
        activeAgencyId: agencyId,
        activeDepartmentId: null,
      };
    });
  }, []);

  const setActiveDepartment = useCallback((departmentId: string) => {
    setAccessControlState((prev) => {
      if (!prev.userContext) return prev;

      const department = prev.userContext.departments.find(
        (entry) => entry.department_id === departmentId
      );
      if (!department) {
        return prev;
      }

      return {
        ...prev,
        userContext: {
          ...prev.userContext,
          active_agency_id: department.agency_id || prev.activeAgencyId || undefined,
          active_department_id: department.department_id,
        },
        activeAgencyId: department.agency_id || prev.activeAgencyId,
        activeDepartmentId: department.department_id,
      };
    });
  }, []);

  useEffect(() => {
    if (accessControlState.loading) {
      return;
    }

    persistActiveOrgScopeCookies({
      activeAgencyId: accessControlState.activeAgencyId,
      activeDepartmentId: accessControlState.activeDepartmentId,
    });
  }, [
    accessControlState.activeAgencyId,
    accessControlState.activeDepartmentId,
    accessControlState.loading,
  ]);

  // Set a single feature flag (supports tri-state: 'disabled' | 'warning' | 'enabled')
  const setFeatureFlag = useCallback((flagKey: string, state: FeatureFlagState) => {
    setFeatureFlagsState((prev) => {
      const newFlags = { ...prev, [flagKey]: state };
      // Persist to localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(newFlags));
        } catch (error) {
          logger.warn('AppShellProvider', 'Failed to save feature flag to localStorage', { error });
        }
      }
      logger.debug('AppShellProvider', 'Feature flag updated', { flagKey, state });
      return newFlags;
    });
  }, []);

  // Set multiple feature flags at once (supports tri-state: 'disabled' | 'warning' | 'enabled')
  const setFeatureFlags = useCallback((flags: Record<string, FeatureFlagState>) => {
    setFeatureFlagsState((prev) => {
      const newFlags = { ...prev, ...flags };
      // Persist to localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(newFlags));
        } catch (error) {
          logger.warn('AppShellProvider', 'Failed to save feature flags to localStorage', { error });
        }
      }
      logger.debug('AppShellProvider', 'Feature flags updated', { flags });
      return newFlags;
    });
  }, []);

  // -------------------------------------------------------------------------
  // Context Value
  // -------------------------------------------------------------------------

  const value: AppShellContextValueWithAuth = useMemo(
    () => ({
      // State
      currentRoute,
      user,
      userContext,
      isAuthLoading,
      sidebarOpen,
      sidebarCollapsed,
      agencyBranding,
      featureFlags,
      layoutOverride,

      // Centralized Auth State (NEW)
      authVersion,
      lastAuthRefresh,
      adminStatus,
      permissionsState,
      accessControlState,

      // Actions
      toggleSidebar,
      setSidebarOpen,
      setSidebarCollapsed,
      setLayoutOverride,
      refreshAuth,
      setFeatureFlag,
      setFeatureFlags,
      setActiveAgency,
      setActiveDepartment,

      // Centralized Auth Actions — both point to same unified fetch
      refetchPermissions: fetchUnifiedContext,
      refetchAccessControl: fetchUnifiedContext,
    }),
    [
      currentRoute,
      user,
      userContext,
      isAuthLoading,
      sidebarOpen,
      sidebarCollapsed,
      agencyBranding,
      featureFlags,
      layoutOverride,
      authVersion,
      lastAuthRefresh,
      adminStatus,
      permissionsState,
      accessControlState,
      toggleSidebar,
      refreshAuth,
      setFeatureFlag,
      setFeatureFlags,
      setActiveAgency,
      setActiveDepartment,
      fetchUnifiedContext,
    ]
  );

  return (
    <AppShellContext.Provider value={value}>
      <HeaderActionsProvider>
        {/* Narrow recovery guard for the Radix body pointer-events leak (#257). */}
        <RadixPointerEventsGuard />
        {/* Initialize notification system (service worker, realtime subscriptions) */}
        <NotificationInitializer
          userId={user?.id ?? null}
          enabled={!!user}
        />
        {/* Masquerade banner — renders above all content when active */}
        <MasqueradeBanner />
        {/* Tier simulator banner — renders above content when simulating a tier */}
        <TierSimulatorBanner />
        {children}
      </HeaderActionsProvider>
    </AppShellContext.Provider>
  );
}

// ============================================================================
// Export
// ============================================================================

export default AppShellProvider;
